import { CheckInStatusEnum } from 'app/shared/enums/check-in-status-type.enum';
import { GridSchema } from '../common/grid-schema.model';
import { LocationFullDTO } from '../../../../shared/models/location.model';
import { PointOfSaleDetailVM } from '../point-of-sale/bo-point-of-sale.model';
import { PointOfSaleDto } from 'app/shared/models/point-of-sale.model';
import { LocationCustomerDTO } from './customer-location.model';
import { CompanyFormVM, CustomerDiscountDTO, PaymentTermVM } from '../back-office-admin-common/customer-check-in.model';
import { CustomerCategoryDTO } from 'app/shared/models/customer-category.model';
import { CustomerTypeEnum } from '../../enum/customer-type-enum.enum.model';
import { VoucherVM } from '../back-office-admin-common/vouchers.model';
import { PaymentTermsByChannelVM } from '../back-office-admin-common/channel-payment-term.model';
import { PackageGroupModeTypeEnum } from '../../enum/package-group-mode-type.enum';
import { ContactTypeEnum } from '../../enum/contact-type.enum.model';
import { StoreCustomerLocationGetDto } from '../point-of-sale/storePointOfSale.model';
import { ContactVM } from '../back-office-admin-common/contact.models';
import { CountryDTO } from 'app/shared/utilities/countries.models';

export interface MissingCustomerFields {
  property: string | number | GeneralFieldsDTO | CustomerCategoryDTO[] | boolean | ContactVM[] | CountryDTO;
  field: string;
  tab: string;
  isRequired: boolean;
}

export interface CheckboxCustomerInfo {
  isCentralPaymentAccount: boolean;
  isInvoicingAccount: boolean;
}

export class CustomerVM {
  id: number;
  vatNumber: string;
  name: string;
  city?: string;
  pointsOfSale?: PointOfSaleDetailVM[];
  onlineStore?: string;
  tier?: string;
  agency?: string;
  orders?: string;
  totalPurchased?: string;
  abi?: string;
  checkInStatus?: CheckInStatusEnum;
  billingLocation?: LocationFullDTO;
  customerLocations?: LocationFullDTO[];
  checked?: boolean;
  externalId: string;
  customerVouchers?: VoucherVM[];
  customerStatus?: CheckInStatusEnum;
  fullName?: string;
  customerGroupCodeId?: number;
  constructor(init?: Partial<CustomerVM>) {
    Object.assign(this, init);
  }
}


export class CustomerVoucherVM implements CustomerVM {
  id: number;
  vatNumber: string;
  name: string;
  city?: string;
  pointsOfSale?: PointOfSaleDetailVM[];
  onlineStore?: string;
  tier?: string;
  agency?: string;
  orders?: string;
  totalPurchased?: string;
  abi?: string;
  checkInStatus?: number;
  billingLocation?: LocationFullDTO;
  checked?: boolean;
  customerVouchers?: VoucherVM[];
  customerVoucherExists?: boolean;
  residualAmount: string;
  externalId: string;

  constructor(init?: Partial<CustomerVoucherVM>) {
    Object.assign(this, init);
  }
}

export class CustomerVMWhenCheck implements CustomerVM {
  abi: string;
  isChecked: boolean;
  id: number;
  vatNumber: string;
  name: string;
  city: string;
  pointsOfSale: PointOfSaleDetailVM[];
  onlineStore: string;
  tier: string;
  agency: string;
  orders: string;
  totalPurchased: string;
  checkInStatus: number;
  externalId: string;
  constructor(init?: Partial<CustomerVMWhenCheck>) {
    Object.assign(this, init);
  }
}

export interface CustomerFullViewModel {
  customer: CustomerFullDTO;
  generalFields: GeneralFieldsDTO[];
}

export class CustomerFullDTO {
  vatNumber: string;
  abi?: string;
  availableAllowance?: string;
  bankAccountId?: string;
  bankAccountNumber?: string;
  bankName?: string;
  basicCardId?: string;
  blockOrderType?: BlockOrderTypeEnum;
  cab?: string;
  centralPaymentNumber?: string;
  centralPaymentParentCustomerId?: number;
  centralPaymentParentCustomerExternalId?: string;
  centralPaymentParentCustomerName?: string;
  checkInMessage?: string;
  checkInStatus?: CheckInStatusEnum;
  commercialAllowance?: number;
  companyForm?: CompanyFormVM;
  companyFormId?: number;
  creditLimit?: number;
  currency?: string;
  currencyType?: CurrencyType;
  customerType?: CustomerTypeEnum;
  externalId?: string;
  fiscalCode?: string;
  glnNumber?: string;
  iban?: string;
  id?: number;
  invoicingParentCustomerExternalId?: string;
  invoicingParentCustomerId?: number;
  invoicingParentCustomerName?: string;
  isActiveEnterprise?: boolean;
  isCentralPaymentAccount?: boolean;
  isCompliantActivity?: boolean;
  isCorrectRegistry?: boolean;
  isFactor?: boolean;
  isInvoicingAccount?: boolean;
  knownAs?: string;
  name?: string;
  notifyStoreId?: number;
  paCode?: string;
  paymentTermId?: number;
  pecEmail?: string;
  paymentTerm?: PaymentTermVM;
  pointsOfSale?: PointOfSaleDto[];
  customerLocations?: LocationCustomerDTO[];
  customerCategories?: CustomerCategoryDTO[];
  customerDiscounts?: CustomerDiscountDTO[];
  lastModificationTime?: string;
  lastBusinessUserModificationTime?: Date;
  lastBusinessUserModifierId?: number;
  creationTime?: string;
  creatorId?: number;
  lastModifierId?: number;
  onlineStore?: string;
  tier?: string;
  agency?: string;
  orders?: string;
  totalPurchased?: string;
  customerVouchers?: VoucherVM[];
  paymentTermOverrides?: PaymentTermsByChannelVM[];
  ordersBlockedByUser?: string;
  acceptsOrders?: boolean;
  packageGroupModeType?: PackageGroupModeTypeEnum;
  sdiCode?: string;
  sirenNumber?: string;
  swift?: string;
  sendToErp?: boolean;
  routingNumber?: string;
  generalFields?: GeneralFieldsDTO[];
  tenantSettings?: CustomerFieldsConfig;
  billingLocationStoreCustomerLocations: StoreCustomerLocationGetDto[];
  hasActiveGokSetting?: boolean;
  constructor(init?: Partial<CustomerFullDTO>) {
    Object.assign(this, init);
  }
}

export interface CustomerFieldsConfig {
  abiRequired: boolean;
  accountNumberRequiredRegister: boolean;
  accountNumberRequiredUpdate: boolean;
  address1Required: boolean;
  address2Required: boolean;
  attachmentRequired: boolean;
  bankAccountIdRequired: boolean;
  bankAccountNumberRequired: boolean;
  bankNameRequired: boolean;
  billingLocationRequired: boolean;
  cabRequired: boolean;
  cityRequired: boolean;
  companyFormRequired: boolean;
  contactTypeRequired: ContactTypeEnum[];
  countryCodeRequired: boolean;
  countryRequired: boolean;
  currencyCodeRequired: boolean;
  customerCategoryLimit: number; // diventato customer classification group
  customerCategoryRequired: boolean;
  emailPecRequired: boolean;
  fiscalCodeRequired: boolean;
  generalFieldsRequired: number[];
  glnNumberRequired: boolean;
  gokSettingsRequired: boolean;
  hasAbi: boolean;
  hasAccountNumberRegister: boolean;
  hasAccountNumberUpdate: boolean;
  hasAddress1: boolean;
  hasAddress2: boolean;
  hasAttachment: boolean;
  hasBankAccountId: boolean;
  hasBankAccountNumber: boolean;
  hasBankName: boolean;
  hasBillingLocation: boolean;
  hasCab: boolean;
  hasCentralPayment: boolean;
  hasCentralPaymentNumber: boolean;
  hasCity: boolean;
  hasCommercialBlock: boolean;
  hasCompanyForm: boolean;
  hasContactDetail: boolean;
  hasCountry: boolean;
  hasCountryCode: boolean;
  hasCurrencyCode: boolean;
  hasCustomerCategory: boolean; // diventato customer classification group
  hasEmailPec: boolean;
  hasFiscalCode: boolean;
  hasGeneralFields: GeneralFieldsTypeEnum[];
  hasGeneralFieldsRequired: GeneralFieldsTypeEnum[];
  hasGlnNumber: boolean;
  hasGokSettings: boolean;
  hasIban: boolean;
  hasInvoicingAccountNumber: boolean;
  hasIsCentralPayment: boolean;
  hasIsFactor: boolean;
  hasIsInvoicing: boolean;
  hasKnownAs: boolean;
  hasName: boolean;
  hasPackageGroupingMode: boolean;
  hasPa: boolean;
  hasPaymentTerm: boolean;
  hasProvince: boolean;
  hasRoutingNumber: boolean;
  hasSdi: boolean;
  hasSirenNumber: boolean;
  hasSwift: boolean;
  hasUserCustomerOwnerRequired: boolean;
  hasVatNumber: boolean;
  hasZipCode: boolean;
  ibanRequired: boolean;
  isFactorRequired: boolean;
  knownHasRequired: boolean;
  nameRequired: boolean;
  packageGroupingModeRequired: boolean;
  paRequired: boolean;
  paymentTermRequired: boolean;
  provinceRequired: boolean;
  routingNumberRequired: boolean;
  sdiRequired: boolean;
  sirenNumberRequired: boolean;
  swiftRequired: boolean;
  validCommercialBlock: number[];
  hasVatNumberUpdate: boolean;
  vatNumberRequiredUpdate: boolean;
	hasVatNumberRegister: boolean;
  vatNumberRequiredRegister: boolean;
  zipCodeRequired: boolean;
}

export class CachedCustomerVM {
  customer: CustomerFullDTO;
  customerCategories?: CustomerCategoryDTO[];
  generalFields?: GeneralFieldsDTO[];
  constructor(init?: Partial<CachedCustomerVM>) {
    Object.assign(this, init);
  }
}

export interface GridCustomersSchema<T> extends GridSchema<T> {
  customerType?: CustomerTypeEnum | null,
  agencyId?: number | null,
  checkInStatus?: CheckInStatusEnum | null,
  storeId?: number;
  onlyBlocked?: boolean;
  blockOrderType?: BlockOrderTypeEnum | BlockOrderTypeEnum[] | null;
  customerGroupCodeId?: number;
  customerGroupId?: number;
  customerHoldingId?: number;
  customerSignboardId?: number;
  customerSubGroupCodeId?: number;
  salesTaxGroupId?: number;
};


export class CompanyReportDTO {
  address: string;
  businessName: string;
  companyNumber: string;
  companyRegistrationNumber: string;
  companyStatus: string;
  companyStatusDescription: string;
  contactCity: string;
  contactCountry: string;
  contactHouseNumber: string;
  contactPostalCode: string;
  contactProvince: string;
  contactSimpleValue: string;
  contactStreet: string;
  country: string;
  creationTime: string;
  currentCreditSafeRating: CreditSafeRating;
  currentCreditSafeRatingId: 1;
  customerId: number;
  emailAddresses: string;
  hasCompaniesInsolvency: boolean;
  hasInsolvency: boolean;
  hasPrejudicials: boolean;
  hasProtesti: boolean;
  hqType: string;
  id: number;
  incorporationDate: string;
  latestRatingChangeDate: string;
  latestTurnoverCurrency: null;
  latestTurnoverValue: 0;
  latestUpdateOnIc: string;
  legalForm: string;
  mainActivityCode: string;
  mainActivityDescription: string;
  numberOfEmployees: string;
  previousCreditSafeRating: CreditSafeRating;
  previousCreditSafeRatingId: number;
  province: string;
  publicRegisterSection: string;
  reaInscriptionDate: string;
  registerStatus: string;
  shareCapital: number;
  taxCode: string;
  telephone: string;
  vatRegistrationNumber: string;
}
export class CreditSafeRating {
  commonDescription: string;
  commonValue: string;
  creditLimitCurrency: string;
  creditLimitValue: number;
  providerDescription: string;
  providerMaxValue: number;
  providerMinValue: number;
  providerValue: number;
  id: number;
}

export class CustomerUpdateDto {
  id: number;
  name: string;
  vatNumber: string;
  fiscalCode: string;
  abi: string;
  cab: string;
  swift: string;
  paCode: string;
  sdiCode: string;
  pecEmail: string;
  isCompliantActivity: boolean;
  isCorrectRegistry: boolean;
  isActiveEnterprise: boolean;
  acceptsOrders: boolean;
  companyFormId: number;
  paymentTermId: number;
  externalId: string;
  customerType: number;
  checkInStatus: number;
  checkInMessage: string;
  basicCardId: string;
  commercialAllowance: number;
  availableAllowance: string;
  blockOrderType: BlockOrderTypeEnum;
  constructor(init?: CustomerUpdateDto) {
    Object.assign(this, init);
  }
}

export class CustomerMultiSelectVM extends CustomerVM {
  declare checked: boolean;
  constructor(init?: CustomerMultiSelectVM) {
    super();
    Object.assign(this, init);
  }
}

export enum BlockOrderTypeEnum {
  UnKnown = 0,
  Unpaid = 1,          // IMPAYE
  Open = 2,            // FREE
  Lawyer = 3,          // AVOCAT
  Cessation = 4,       // CESSATION
  CtxSfact = 5,        // CTX_SFAC
  Encours = 6,         // DOTATION
  ClosedRep = 7,       // FERME REP
  JudicalLiquidation = 8, // LIQUID_JUD
  JudicalRecovery = 9,    // REDRES_JUD
  PaymentDelay = 10,      // RETARD_PAI
  ValidRep = 11,          // VALID REP
  Blocked = 12,           // BLOCKED
  DtxUk = 13,             // DTX-UK
  MauvaisCl = 14          // MAUVAIS_CL
}

export interface BlockOrderTypeItem {
  id: BlockOrderTypeEnum;
  value: string;
}

export enum GeneralFieldsTypeEnum{
  PaymentMethodCode = 1,
  CustomerGroup = 2,
  SalesTaxGroup = 3,
  TransactionType = 4,
  DefaultTransactionSpecification = 5,
  GroupOfReps = 6,
  CustomerGroupCode = 7,
  CustomerSubGroupCode = 8,
  CustomerSignBoard = 9,
  CustomerHolding = 10,
  DeliveryTerms = 11,
  CancellationReason = 12,
  RoutingType = 13
}

export enum GokStatusEnum {
  Active = 0,
  Inactive = 1
}

export enum CurrencyType
{
  Eur = 1,
  Usd = 2,
  Gbp = 3
}

export enum PackageGroupModeType
{
  ByOrder = 1,
  ByRefCol = 2
}

export class GeneralFieldsDTO{
  id?: number;
  description?: string;
  decimalValue?: number;
  code: string;
  note?: string;
  creationTime?: Date;
  lastModificationTime?: Date;
  type?: GeneralFieldsTypeEnum;

  constructor(init?: GeneralFieldsDTO) {
    Object.assign(this, init);
  }
}

export class GeneralFieldsVM extends GeneralFieldsDTO {
  fullname?: string;
  constructor(init?: GeneralFieldsVM) {
    super();
    Object.assign(this, init);
  }
}